import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Languages, Pencil, RotateCcw, History, X, Check } from "lucide-react";
import { toast } from "sonner";
import { i18n, resources, unflattenKeys, type OverridesPayload } from "@/i18n";
import { useRequireOwner } from "@/hooks/useRequireOwner";
import { parseApiError } from "@/lib/api-error";
import {
  upsertOverride,
  deleteOverride,
  fetchOverrideHistory,
} from "@/lib/api-client";
import { formatDateTime } from "@/lib/formatters";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";

interface TranslationRow {
  namespace: string;
  key: string;
  deValue: string;
  enValue: string;
  deOverride: boolean;
  enOverride: boolean;
}

interface HistoryEntry {
  id: string;
  language: string;
  namespace: string;
  key: string;
  old_value: string | null;
  new_value: string | null;
  action: "insert" | "update" | "delete";
  changed_at: string;
  changed_by: string;
}

const ACTION_KEY_MAP = {
  insert: "translations.historyInsert",
  update: "translations.historyUpdate",
  delete: "translations.historyDelete",
} as const satisfies Record<HistoryEntry["action"], string>;

/**
 * Flatten i18next store data into table rows, marking which have overrides.
 */
function buildRows(overrides: OverridesPayload | null): TranslationRow[] {
  const rows: Map<string, TranslationRow> = new Map();
  const store = i18n.store?.data;
  if (!store) return [];

  for (const lng of ["de", "en"] as const) {
    const lngData = store[lng];
    if (!lngData) continue;
    for (const ns of Object.keys(lngData)) {
      const nsData = lngData[ns];
      if (!nsData) continue;
      flattenKeys(nsData as Record<string, unknown>, "").forEach(
        ([key, value]) => {
          const rowKey = `${ns}.${key}`;
          const existing = rows.get(rowKey) ?? {
            namespace: ns,
            key,
            deValue: "",
            enValue: "",
            deOverride: false,
            enOverride: false,
          };
          if (lng === "de") {
            existing.deValue = String(value);
            existing.deOverride = !!overrides?.de?.[ns]?.[key];
          } else {
            existing.enValue = String(value);
            existing.enOverride = !!overrides?.en?.[ns]?.[key];
          }
          rows.set(rowKey, existing);
        },
      );
    }
  }

  return Array.from(rows.values()).sort((a, b) => {
    const nsCmp = a.namespace.localeCompare(b.namespace);
    return nsCmp !== 0 ? nsCmp : a.key.localeCompare(b.key);
  });
}

function flattenKeys(
  obj: Record<string, unknown>,
  prefix: string,
): [string, string][] {
  const entries: [string, string][] = [];
  for (const [k, v] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "object" && v !== null && !Array.isArray(v)) {
      entries.push(...flattenKeys(v as Record<string, unknown>, fullKey));
    } else {
      entries.push([fullKey, String(v ?? "")]);
    }
  }
  return entries;
}

export default function AdminTranslationsPage() {
  const { t } = useTranslation("admin");
  const { isOwner, isLoading: authLoading } = useRequireOwner();
  const queryClient = useQueryClient();

  // Filters
  const [search, setSearch] = useState("");
  const [nsFilter, setNsFilter] = useState<string>("__all__");
  const [lngFilter, setLngFilter] = useState<string>("__all__");

  // Editing state
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editLng, setEditLng] = useState<"de" | "en">("de");
  const [editValue, setEditValue] = useState("");
  const editTriggerRef = useRef<HTMLElement | null>(null);

  // Reset confirmation dialog
  const [resetTarget, setResetTarget] = useState<{
    row: TranslationRow;
    lng: "de" | "en";
  } | null>(null);

  // History drawer
  const [historyKey, setHistoryKey] = useState<{
    ns: string;
    key: string;
  } | null>(null);
  const [historyEntries, setHistoryEntries] = useState<HistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Counter to force useMemo re-eval after addResourceBundle
  const [storeVersion, setStoreVersion] = useState(0);

  // Query for overrides — seeded from boot data (ADR-2)
  const { data: overrides } = useQuery<OverridesPayload>({
    queryKey: ["i18n-overrides"],
    queryFn: async (): Promise<OverridesPayload> => {
      const res = await fetch("/api/i18n/overrides");
      if (!res.ok) return {};
      return (await res.json()) as OverridesPayload;
    },
    initialData: () =>
      (window.__I18N_BOOT_OVERRIDES__ as OverridesPayload) ?? {},
    staleTime: 30_000,
  });

  // Build rows from i18next store
  const allRows = useMemo(
    () => buildRows(overrides ?? null),
    // storeVersion forces re-eval after addResourceBundle mutations
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [overrides, storeVersion],
  );

  // Filter rows
  const filteredRows = useMemo(() => {
    let rows = allRows;
    if (nsFilter !== "__all__") {
      rows = rows.filter((r) => r.namespace === nsFilter);
    }
    if (lngFilter === "de") {
      rows = rows.filter((r) => r.deOverride);
    } else if (lngFilter === "en") {
      rows = rows.filter((r) => r.enOverride);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.key.toLowerCase().includes(q) ||
          r.namespace.toLowerCase().includes(q) ||
          r.deValue.toLowerCase().includes(q) ||
          r.enValue.toLowerCase().includes(q),
      );
    }
    return rows;
  }, [allRows, nsFilter, lngFilter, search]);

  // Upsert mutation
  const upsertMutation = useMutation({
    mutationFn: upsertOverride,
    onSuccess: (_data, variables) => {
      // Apply override to i18next store immediately.
      // Unflatten dotted key so i18next's keySeparator resolution works.
      const nested = unflattenKeys({ [variables.key]: variables.value });
      i18n.addResourceBundle(
        variables.language,
        variables.namespace,
        nested,
        true,
        true,
      );
      setStoreVersion((v) => v + 1);
      queryClient.invalidateQueries({ queryKey: ["i18n-overrides"] });
      toast.success(t("translations.saved"));
      setEditingKey(null);
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: ({
      language,
      namespace,
      key,
    }: {
      language: string;
      namespace: string;
      key: string;
    }) => deleteOverride(language, namespace, key),
    onSuccess: (_data, variables) => {
      // Restore original bundled value so the i18next store no longer
      // holds the deleted override. Without this, buildRows reads the
      // stale override from i18n.store.data until a full page reload.
      const lng = variables.language as keyof typeof resources;
      const ns = variables.namespace;
      const bundled =
        resources[lng]?.[ns as keyof (typeof resources)[typeof lng]];
      if (bundled) {
        i18n.addResourceBundle(lng, ns, bundled, true, true);
      }
      setStoreVersion((v) => v + 1);
      queryClient.invalidateQueries({ queryKey: ["i18n-overrides"] });
      toast.success(t("translations.resetSuccess"));
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message);
    },
  });

  const startEdit = useCallback(
    (row: TranslationRow, lng: "de" | "en", triggerEl?: HTMLElement) => {
      const compositeKey = `${row.namespace}.${row.key}.${lng}`;
      setEditingKey(compositeKey);
      setEditLng(lng);
      setEditValue(lng === "de" ? row.deValue : row.enValue);
      editTriggerRef.current = triggerEl ?? null;
    },
    [],
  );

  const cancelEdit = useCallback(() => {
    setEditingKey(null);
    // Return focus to the element that triggered editing
    editTriggerRef.current?.focus();
    editTriggerRef.current = null;
  }, []);

  const saveEdit = useCallback(
    (row: TranslationRow) => {
      upsertMutation.mutate({
        language: editLng,
        namespace: row.namespace,
        key: row.key,
        value: editValue,
      });
    },
    [editLng, editValue, upsertMutation],
  );

  const handleReset = useCallback((row: TranslationRow, lng: "de" | "en") => {
    setResetTarget({ row, lng });
  }, []);

  const confirmReset = useCallback(() => {
    if (!resetTarget) return;
    deleteMutation.mutate({
      language: resetTarget.lng,
      namespace: resetTarget.row.namespace,
      key: resetTarget.row.key,
    });
    setResetTarget(null);
  }, [resetTarget, deleteMutation]);

  const openHistory = useCallback(
    async (ns: string, key: string) => {
      setHistoryKey({ ns, key });
      setHistoryLoading(true);
      setHistoryEntries([]);
      try {
        // CST202: routed through backend (require_owner-guarded) instead of
        // direct Supabase, which was readable by every company admin via RLS.
        const data = await fetchOverrideHistory(ns, key, 50);
        setHistoryEntries(
          (data as HistoryEntry[]).filter(
            (e) => e.id && e.action && e.changed_at,
          ),
        );
      } catch (err) {
        console.error("[AdminTranslations] history load failed", err);
        toast.error(parseApiError(err).message);
      } finally {
        setHistoryLoading(false);
      }
    },
    [t],
  );

  // Unique namespaces from data
  const namespaces = useMemo(
    () => [...new Set(allRows.map((r) => r.namespace))].sort(),
    [allRows],
  );

  if (authLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-muted-foreground">{t("common.loading")}</p>
      </div>
    );
  }

  if (!isOwner) return null;

  return (
    <div className="container mx-auto space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Languages className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">{t("translations.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("translations.subtitle")}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder={t("translations.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64"
          data-testid="translations-search"
        />
        <Select value={nsFilter} onValueChange={setNsFilter}>
          <SelectTrigger className="w-48" data-testid="translations-ns-filter">
            <SelectValue placeholder={t("translations.namespace")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">
              {t("translations.allNamespaces")}
            </SelectItem>
            {namespaces.map((ns) => (
              <SelectItem key={ns} value={ns}>
                {ns}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={lngFilter} onValueChange={setLngFilter}>
          <SelectTrigger className="w-48" data-testid="translations-lng-filter">
            <SelectValue placeholder={t("translations.language")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">
              {t("translations.allLanguages")}
            </SelectItem>
            <SelectItem value="de">
              {t("translations.filterOverridesDe")}
            </SelectItem>
            <SelectItem value="en">
              {t("translations.filterOverridesEn")}
            </SelectItem>
          </SelectContent>
        </Select>
        <span
          className="text-sm text-muted-foreground"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {t("translations.showing", { count: filteredRows.length })}
        </span>
      </div>

      {/* Table */}
      {filteredRows.length === 0 ? (
        <p className="py-8 text-center text-muted-foreground">
          {t("translations.emptyState")}
        </p>
      ) : (
        <div className="rounded-md border">
          <Table aria-label={t("translations.tableAriaLabel")}>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">
                  {t("translations.key")}
                </TableHead>
                <TableHead>{t("translations.deValue")}</TableHead>
                <TableHead>{t("translations.enValue")}</TableHead>
                <TableHead className="w-[100px]">
                  {t("translations.status")}
                </TableHead>
                <TableHead className="w-[160px]">
                  {t("translations.actions")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.map((row) => {
                const deEditKey = `${row.namespace}.${row.key}.de`;
                const enEditKey = `${row.namespace}.${row.key}.en`;
                const isEditingDe = editingKey === deEditKey;
                const isEditingEn = editingKey === enEditKey;
                const hasOverride = row.deOverride || row.enOverride;

                return (
                  <TableRow
                    key={`${row.namespace}.${row.key}`}
                    data-testid="translation-row"
                  >
                    <TableCell className="font-mono text-xs">
                      <span className="text-muted-foreground">
                        {row.namespace}.
                      </span>
                      {row.key}
                    </TableCell>
                    <TableCell>
                      {isEditingDe ? (
                        <div className="flex items-start gap-1">
                          <Textarea
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="min-h-[60px] text-sm"
                            autoFocus
                            aria-label={`${t("translations.edit")} DE: ${row.namespace}.${row.key}`}
                            data-testid="edit-textarea"
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => saveEdit(row)}
                            disabled={upsertMutation.isPending}
                            aria-label={t("translations.save")}
                            data-testid="save-edit"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={cancelEdit}
                            aria-label={t("translations.cancel")}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <span
                          className="cursor-pointer text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:rounded"
                          onClick={(e) => startEdit(row, "de", e.currentTarget)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              startEdit(
                                row,
                                "de",
                                e.currentTarget as HTMLElement,
                              );
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          title={t("translations.edit")}
                        >
                          {row.deValue || "\u2014"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditingEn ? (
                        <div className="flex items-start gap-1">
                          <Textarea
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="min-h-[60px] text-sm"
                            autoFocus
                            aria-label={`${t("translations.edit")} EN: ${row.namespace}.${row.key}`}
                            data-testid="edit-textarea"
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => saveEdit(row)}
                            disabled={upsertMutation.isPending}
                            aria-label={t("translations.save")}
                            data-testid="save-edit"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={cancelEdit}
                            aria-label={t("translations.cancel")}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <span
                          className="cursor-pointer text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:rounded"
                          onClick={(e) => startEdit(row, "en", e.currentTarget)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              startEdit(
                                row,
                                "en",
                                e.currentTarget as HTMLElement,
                              );
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          title={t("translations.edit")}
                        >
                          {row.enValue || "\u2014"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {hasOverride ? (
                        <Badge variant="default" className="text-xs">
                          {t("translations.override")}
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          {t("translations.default")}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) =>
                            startEdit(row, "de", e.currentTarget as HTMLElement)
                          }
                          aria-label={`${t("translations.edit")} DE: ${row.namespace}.${row.key}`}
                          title={`${t("translations.edit")} (DE)`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        {row.deOverride && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleReset(row, "de")}
                            aria-label={`${t("translations.reset")} DE: ${row.namespace}.${row.key}`}
                            title={`${t("translations.reset")} (DE)`}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {row.enOverride && (
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleReset(row, "en")}
                            aria-label={`${t("translations.reset")} EN: ${row.namespace}.${row.key}`}
                            title={`${t("translations.reset")} (EN)`}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => openHistory(row.namespace, row.key)}
                          aria-label={`${t("translations.history")}: ${row.namespace}.${row.key}`}
                          title={t("translations.history")}
                        >
                          <History className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      <AlertDialog
        open={resetTarget !== null}
        onOpenChange={(open) => !open && setResetTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("translations.reset")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("translations.resetConfirm")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("translations.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReset}>
              {t("translations.reset")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History Drawer */}
      <Sheet
        open={historyKey !== null}
        onOpenChange={(open) => !open && setHistoryKey(null)}
      >
        <SheetContent side="right" className="w-[500px] sm:max-w-[500px]">
          <SheetHeader>
            <SheetTitle>
              {historyKey
                ? t("translations.historyFor", {
                    key: `${historyKey.ns}.${historyKey.key}`,
                  })
                : t("translations.history")}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3 overflow-y-auto">
            {historyLoading ? (
              <p className="text-sm text-muted-foreground">
                {t("common.loading")}
              </p>
            ) : historyEntries.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t("translations.noHistory")}
              </p>
            ) : (
              historyEntries.map((entry) => (
                <div key={entry.id} className="rounded-lg border p-3 text-sm">
                  <div className="mb-1 flex items-center justify-between">
                    <Badge
                      variant={
                        entry.action === "delete"
                          ? "destructive"
                          : entry.action === "insert"
                            ? "default"
                            : "secondary"
                      }
                      className="text-xs"
                    >
                      {t(ACTION_KEY_MAP[entry.action])}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      [{entry.language}] {formatDateTime(entry.changed_at)}
                    </span>
                  </div>
                  {entry.old_value !== null && (
                    <div className="mt-1">
                      <span className="text-muted-foreground">
                        {t("translations.historyOldValue")}:{" "}
                      </span>
                      <span className="line-through">{entry.old_value}</span>
                    </div>
                  )}
                  {entry.new_value !== null && (
                    <div className="mt-1">
                      <span className="text-muted-foreground">
                        {t("translations.historyNewValue")}:{" "}
                      </span>
                      <span className="font-medium">{entry.new_value}</span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
